// Shared metadata for external "Ulkoiset palvelut" — commercial data services that enrich
// optimization (weather/price/CO₂/demand-response). Both the configuration step
// (ServicesSection) and the Konfiguraation tila overview read this so labels stay consistent.
// These are display labels only; the real per-building activation state comes from
// listBuildingServices (BuildingService.is_active / last_synced_at).
import type { BuildingService, BuildingServiceType } from "@/lib/api/building";

export interface ServiceMeta {
  type: BuildingServiceType;
  label: string;
  provider: string;
  description: string;
  signals: string;
  unlocks: string;
}

// Display order for the four known service types.
export const SERVICE_META_ORDER: BuildingServiceType[] = [
  "weather-preconditioning",
  "electricity-price-optimization",
  "carbon-intensity",
  "demand-response",
];

// Canonical data_point_history names each service's collector writes (building_domain
// collector.py). Single source of truth for "which signals belong to which service" —
// SERVICE_META.signals (display text) and Signaalit's Ulkoiset palvelut visibility filter
// (bug #1506: hide a service's rows there while it is inactive) both derive from this.
export const SERVICE_SIGNAL_NAMES: Record<BuildingServiceType, string[]> = {
  "weather-preconditioning": ["outdoor_temperature_c", "solar_radiation_wm2"],
  "electricity-price-optimization": ["electricity_price_eur_kwh"],
  "carbon-intensity": ["grid_co2_intensity_gco2_kwh"],
  "demand-response": ["demand_response_event"],
};

export const SERVICE_META: Record<BuildingServiceType, ServiceMeta> = {
  "weather-preconditioning": {
    type: "weather-preconditioning",
    label: "Säädata & -ennuste",
    provider: "Open-Meteo",
    description:
      "Ulkoilman lämpötila ja auringonsäteily nyt ja 24 tunnin ennuste.",
    signals: SERVICE_SIGNAL_NAMES["weather-preconditioning"].join(" · "),
    unlocks: "Sääennusteoptimointi · Käyttöaikainen optimointi",
  },
  "electricity-price-optimization": {
    type: "electricity-price-optimization",
    label: "Sähkön hinta",
    provider: "Pörssisähkö.net",
    description: "Spot-hinta nyt ja seuraavien 24 tunnin tuntihinnat.",
    signals: SERVICE_SIGNAL_NAMES["electricity-price-optimization"].join(" · "),
    unlocks: "Sähkön hintaoptimointi · Kuormanjousto",
  },
  "carbon-intensity": {
    type: "carbon-intensity",
    label: "CO₂-intensiteetti",
    provider: "Fingrid",
    description: "Sähköverkon hiili-intensiteetti nyt ja ennuste.",
    signals: SERVICE_SIGNAL_NAMES["carbon-intensity"].join(" · "),
    unlocks: "CO₂-optimointi",
  },
  "demand-response": {
    type: "demand-response",
    label: "Kuormanjousto",
    provider: "Fingrid",
    description: "Kysyntäjouston tapahtumat ja tehorajasignaalit.",
    signals: SERVICE_SIGNAL_NAMES["demand-response"].join(" · "),
    unlocks: "Kuormanjousto · Huipputehon leikkaus",
  },
};

// Names of every external-feed signal whose owning service is currently active — used to hide
// an inactive service's rows from the Signaalit Ulkoiset palvelut group (bug #1506) without
// touching collection itself (the collector keeps writing regardless of is_active).
export function activeExternalSignalNames(
  services: readonly BuildingService[],
): ReadonlySet<string> {
  return new Set(
    services
      .filter((s) => s.is_active)
      .flatMap((s) => SERVICE_SIGNAL_NAMES[s.service_type] ?? []),
  );
}

// Human label for a service type, tolerant of unknown types the backend might add later.
export function serviceLabel(type: BuildingServiceType): string {
  return SERVICE_META[type]?.label ?? type;
}

// Order index for sorting; unknown types sort last.
export function serviceOrderIndex(type: BuildingServiceType): number {
  const i = SERVICE_META_ORDER.indexOf(type);
  return i === -1 ? SERVICE_META_ORDER.length : i;
}

// Relative Finnish time for an ISO timestamp (assumes a valid, non-null value).
export function formatRelativeFi(iso: string): string {
  const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (diffMin < 1) return "juuri nyt";
  if (diffMin < 60) return `${diffMin} min sitten`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} h sitten`;
  return new Date(iso).toLocaleDateString("fi-FI");
}

// Sync-freshness label for a service; null → not-yet-synced copy.
export function formatServiceSync(iso: string | null): string {
  if (!iso) return "Ei vielä synkronoitu";
  return formatRelativeFi(iso);
}
